import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CartPanel from '@/components/CartPanel';
import { publicApi, clickCollectApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { ShoppingBag, Plus, CalendarIcon, Clock, Leaf, Flame } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function MenuItemCard({ item, addToCart, formatPrice }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-garden-800/5 shadow-sm menu-card" data-testid={`order-item-${item.id}`}>
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-heading font-semibold text-lg text-garden-800 flex-1">{item.name}</h3>
        {(!item.variants || item.variants.length === 0) && (
          <span className="font-heading font-bold text-lg text-garden-800 ml-2">{formatPrice(item.price)}</span>
        )}
      </div>
      {item.description && (
        <p className="text-sm text-charcoal/70 mb-3 leading-relaxed line-clamp-2">{item.description}</p>
      )}
      {item.tags && item.tags.length > 0 && (
        <div className="flex gap-1.5 mb-3">
          {item.tags.map((t) => (
            <Badge key={t} variant="secondary" className="text-xs bg-garden-100 text-garden-800 border-0">
              {t === 'végétarien' && <Leaf size={10} className="mr-1" />}
              {t === 'epicé' && <Flame size={10} className="mr-1" />}
              {t}
            </Badge>
          ))}
        </div>
      )}
      {item.variants && item.variants.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {item.variants.map((v) => (
            <Button
              key={v.name}
              onClick={() => addToCart(item, v.name)}
              size="sm"
              data-testid={`order-add-${item.id}-${v.name}`}
              className="bg-garden-100 text-garden-800 hover:bg-garden-200 rounded-full text-xs"
            >
              <Plus size={12} className="mr-1" /> {v.name} — {formatPrice(v.price)}
            </Button>
          ))}
        </div>
      ) : (
        <Button
          onClick={() => addToCart(item)}
          size="sm"
          data-testid={`order-add-${item.id}`}
          className="bg-garden-800 hover:bg-garden-900 text-white rounded-full text-sm mt-1"
        >
          <Plus size={14} className="mr-1" /> Ajouter
        </Button>
      )}
    </div>
  );
}

function CheckoutStep({ date, setDate, slots, slotsLoading, selectedSlot, setSelectedSlot, form, setForm, notes, setNotes, loading, handleCheckout, setStep, cartTotal, formatPrice, gdprConsent, setGdprConsent }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm">
        <h2 className="font-heading font-semibold text-xl text-garden-800 mb-4 flex items-center gap-2">
          <CalendarIcon size={20} className="text-garden-500" /> Retrait
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-sm mb-2 block">Date de retrait *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="order-date-picker" className="w-full justify-start border-garden-200 rounded-xl h-11">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'EEEE d MMMM', { locale: fr }) : 'Choisir une date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} locale={fr} />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-sm mb-2 block">Créneau de retrait *</Label>
            {slotsLoading ? (
              <div className="flex flex-wrap gap-2 animate-pulse" data-testid="slots-skeleton">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-9 w-24 bg-garden-100 rounded-full" />
                ))}
              </div>
            ) : !date ? (
              <p className="text-sm text-charcoal/50">Sélectionnez d'abord une date</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-red-500">Aucun créneau disponible ce jour</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    onClick={() => setSelectedSlot(s.time)}
                    data-testid={`order-slot-${s.time}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedSlot === s.time
                        ? 'bg-garden-800 text-white'
                        : 'bg-garden-100 text-garden-800 hover:bg-garden-200'
                    }`}
                  >
                    <Clock size={12} className="inline mr-1" /> {s.time}
                    <span className="text-xs ml-1 opacity-60">({s.available} places)</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm">
        <h2 className="font-heading font-semibold text-xl text-garden-800 mb-4">Vos coordonnées</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm mb-1 block">Nom *</Label>
            <Input data-testid="order-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="border-garden-200 rounded-xl h-11" placeholder="Votre nom" />
          </div>
          <div>
            <Label className="text-sm mb-1 block">Téléphone *</Label>
            <Input data-testid="order-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="border-garden-200 rounded-xl h-11" placeholder="06 ..." />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-sm mb-1 block">Email *</Label>
          <Input type="email" data-testid="order-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="border-garden-200 rounded-xl h-11" placeholder="votre@email.com" />
        </div>
        <div className="mt-4">
          <Label className="text-sm mb-1 block">Notes (optionnel)</Label>
          <Input data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="border-garden-200 rounded-xl h-11" placeholder="Instructions spéciales..." />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-2">
          <input id="order-gdpr-consent" type="checkbox" checked={gdprConsent} onChange={(e) => setGdprConsent(e.target.checked)} className="mt-1 h-4 w-4 rounded border-garden-300 text-garden-800 focus:ring-garden-800" />
          <Label htmlFor="order-gdpr-consent" className="text-xs text-charcoal/80 cursor-pointer leading-normal">
            J'accepte les <Link to="/cgv" className="text-garden-800 underline hover:text-garden-900 font-medium">Conditions Générales de Vente (CGV)</Link> et j'accepte que mes données soient traitées par Côté Terrasse pour la gestion de ma commande conformément à la Politique de Confidentialité. Elles seront conservées pendant la durée légale de traitement.
          </Label>
        </div>
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => setStep('menu')} className="rounded-full border-garden-200 h-11">
            Modifier la commande
          </Button>
          <Button
            onClick={handleCheckout}
            disabled={loading}
            data-testid="order-pay-btn"
            className="flex-1 bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium shadow-lg flex flex-col items-center justify-center gap-0.5"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Redirection...</span>
              </div>
            ) : (
              <>
                <span>Confirmer et Payer ({formatPrice(cartTotal)})</span>
                <span className="text-[10px] opacity-80 uppercase tracking-wider font-semibold">Commande avec obligation de paiement</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Order() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState({ categories: [], formules: [] });
  const [ccSettings, setCcSettings] = useState({});
  const [settings, setSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [step, setStep] = useState('menu');
  const [gdprConsent, setGdprConsent] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time init on mount; stable module-level API refs
    publicApi.getMenu().then((r) => {
      setMenu(r.data);
      if (r.data.categories && r.data.categories.length > 0) {
        setActiveCategory(r.data.categories[0].id);
      }
    }).catch(() => {});
    publicApi.getSettings().then((r) => setSettings(r.data)).catch(() => {});
    clickCollectApi.getSettings().then((r) => setCcSettings(r.data)).catch(() => {}).finally(() => setSettingsLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true);
    clickCollectApi.getSlots(format(date, 'yyyy-MM-dd'))
      .then((r) => { setSlots(r.data.slots || []); setSelectedSlot(''); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const addToCart = (item, variantName) => {
    setCart((prev) => {
      const key = item.id + '-' + (variantName || 'default');
      const existing = prev.find((c) => c.key === key);
      if (existing) {
        return prev.map((c) => c.key === key ? { ...c, quantity: c.quantity + 1 } : c);
      }
      const price = variantName
        ? (item.variants.find((v) => v.name === variantName) || {}).price || item.price
        : item.price;
      return [...prev, { key, item_id: item.id, name: item.name, variant_name: variantName || null, price, quantity: 1 }];
    });
    toast.success((item.name) + (variantName ? ' (' + variantName + ')' : '') + ' ajouté');
  };

  const updateQty = (key, delta) => {
    setCart((prev) => prev
      .map((c) => c.key === key ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c)
      .filter((c) => c.quantity > 0)
    );
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const cartCount = useMemo(() => {
    return cart.reduce((sum, c) => sum + c.quantity, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const formatPrice = (p) => {
    if (p === null || p === undefined) return '';
    return Number.isInteger(p) ? p + ' €' : p.toFixed(2).replace('.', ',') + ' €';
  };

  const handleCheckout = async () => {
    if (cart.length === 0) { toast.error('Votre panier est vide'); return; }
    if (!date) { toast.error('Choisissez une date de retrait'); return; }
    if (!selectedSlot) { toast.error('Choisissez un créneau de retrait'); return; }
    if (!form.name || !form.email || !form.phone) { toast.error('Remplissez vos coordonnées'); return; }
    if (!gdprConsent) { toast.error("Veuillez accepter le traitement de vos données."); return; }
    setLoading(true);
    try {
      const orderData = {
        items: cart.map((c) => ({ item_id: c.item_id, name: c.name, quantity: c.quantity, variant_name: c.variant_name })),
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        pickup_date: format(date, 'yyyy-MM-dd'),
        pickup_time: selectedSlot,
        notes: notes,
        origin_url: window.location.origin,
      };
      const res = await clickCollectApi.createOrder(orderData);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la commande');
    }
    setLoading(false);
  };

  if (!settingsLoaded) {
    return (
      <div className="min-h-screen bg-cream" data-testid="order-page-loading">
        <Navbar />
        <div className="pt-28 pb-20 flex justify-center">
          <div className="w-10 h-10 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!ccSettings.enabled) {
    navigate('/commandes-indisponibles', { replace: true });
    return null;
  }

  const activeCat = menu.categories.find((c) => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-cream" data-testid="order-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">Click & Collect</span>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-garden-800">
                {step === 'menu' ? 'Commander en ligne' : 'Finaliser la commande'}
              </h1>
            </div>
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button data-testid="cart-toggle-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full relative">
                  <ShoppingBag size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-sunny text-charcoal text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream w-[320px] p-0">
                <CartPanel
                  cart={cart} cartTotal={cartTotal} updateQty={updateQty}
                  formatPrice={formatPrice} onCheckout={() => { setCartOpen(false); setStep('checkout'); }} step={step}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Barre de progression */}
          <div className="max-w-md mx-auto mb-10 px-4" data-testid="checkout-progress">
            <div className="relative flex items-center justify-between">
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-garden-200 dark:bg-zinc-800 -z-10" />
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-garden-800 transition-all duration-300 -z-10"
                style={{
                  width: step === 'menu' ? '0%' : step === 'checkout' && !loading ? '50%' : '100%'
                }}
              />
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step === 'menu' ? 'bg-garden-800 text-white ring-4 ring-garden-800/10' : 'bg-garden-800 text-white'
                }`}>1</div>
                <span className="text-[11px] font-medium text-garden-900">Panier</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step === 'checkout' ? 'bg-garden-800 text-white ring-4 ring-garden-800/10' : step === 'menu' ? 'bg-white border border-garden-200 text-charcoal/50' : 'bg-garden-800 text-white'
                }`}>2</div>
                <span className={`text-[11px] font-medium ${step === 'checkout' ? 'text-garden-900' : 'text-charcoal/45'}`}>Coordonnées</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  loading ? 'bg-garden-800 text-white ring-4 ring-garden-800/10' : 'bg-white border border-garden-200 text-charcoal/50'
                }`}>3</div>
                <span className={`text-[11px] font-medium ${loading ? 'text-garden-900' : 'text-charcoal/45'}`}>Paiement</span>
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {step === 'menu' ? (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-none">
                    {menu.categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        data-testid={`order-category-${cat.slug}`}
                        className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all shrink-0 ${
                          activeCategory === cat.id
                            ? 'bg-garden-800 text-white shadow-md'
                            : 'bg-white text-charcoal hover:bg-garden-100 border border-garden-200'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                  {activeCat && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {activeCat.items && activeCat.items.map((item) => (
                        <MenuItemCard key={item.id} item={item} addToCart={addToCart} formatPrice={formatPrice} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <CheckoutStep
                  date={date} setDate={setDate} slots={slots} slotsLoading={slotsLoading}
                  selectedSlot={selectedSlot} setSelectedSlot={setSelectedSlot}
                  form={form} setForm={setForm} notes={notes} setNotes={setNotes}
                  loading={loading} handleCheckout={handleCheckout} setStep={setStep}
                  cartTotal={cartTotal} formatPrice={formatPrice}
                  gdprConsent={gdprConsent} setGdprConsent={setGdprConsent}
                />
              )}
            </div>
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <CartPanel
                  cart={cart} cartTotal={cartTotal} updateQty={updateQty}
                  formatPrice={formatPrice} onCheckout={() => setStep('checkout')} step={step}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-[11px] text-charcoal/50 text-center space-y-2 mt-12 mb-6 animate-fade-up">
        <p>Prix nets exprimes en Euros TTC. Conformement au decret n° 2015-447, les informations sur les allergenes presents dans nos plats sont disponibles sur simple demande ou consultables directement sur notre <Link to="/allergenes" className="underline hover:text-garden-800">Page des Allergenes</Link>.</p>
        {(settings.alcohol_licence || settings.meat_origin_info) && (
          <p className="border-t border-garden-800/5 pt-2 max-w-2xl mx-auto">
            {settings.alcohol_licence && <span className="block mb-1">Vente de boissons alcoolisees : {settings.alcohol_licence}</span>}
            {settings.meat_origin_info && <span className="block text-[10px]">Origine des viandes : {settings.meat_origin_info}</span>}
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
}
